import { useEffect, useState, type FormEvent } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { PlatformOutletContext } from '../../app/platform-context.js'
import { useAuthStore } from '../../shared/store/auth.store.js'
import {
  createPlatformMembership,
  deletePlatformMembership,
  listPlatformMemberships,
  updatePlatformMembership,
  type PlatformMembership,
  type PlatformMembershipListResult,
  type PlatformRole,
} from '../../shared/api/platform-memberships.js'
import { ApiError } from '../../shared/types/api.js'
import { formatDate } from '../../shared/utils/format.js'
import SidePanel from '../../shared/ui/SidePanel.js'
import { isPlatformSuperadminRole } from '../../shared/utils/permissions.js'
import {
  usePlatformApiError,
  type PlatformApiErrorKind,
} from '../../app/usePlatformApiError.js'

const EMPTY: PlatformMembershipListResult = {
  data: [],
  meta: { total: 0, page: 1, pageSize: 20, totalPages: 1 },
}

const PAGE_SIZE = 20
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

interface PanelState {
  mode: 'create' | 'edit'
  membership?: PlatformMembership
}

export default function PlatformAdministratorsPage() {
  const { t, i18n } = useTranslation()
  const handleApiError = usePlatformApiError()
  const { platformRole } = useOutletContext<PlatformOutletContext>()
  const currentUserId = useAuthStore(s => s.user?.id)
  const canGrantSuperadmin = isPlatformSuperadminRole(platformRole)

  const [result, setResult] = useState<PlatformMembershipListResult>(EMPTY)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [page, setPage] = useState(1)
  const [reloadKey, setReloadKey] = useState(0)
  const [listErrorKind, setListErrorKind] = useState<PlatformApiErrorKind>('unknown')

  const [panel, setPanel] = useState<PanelState | null>(null)
  const [targetUserId, setTargetUserId] = useState('')
  const [targetRole, setTargetRole] = useState<PlatformRole>('ADMIN')
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState('')
  const [revokingId, setRevokingId] = useState<string | null>(null)

  const availableRoles: PlatformRole[] = canGrantSuperadmin
    ? ['SUPERADMIN', 'ADMIN', 'MANAGER']
    : ['ADMIN', 'MANAGER']

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    listPlatformMemberships({
      page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
      role: roleFilter ? roleFilter as PlatformRole : undefined,
    })
      .then(data => {
        if (cancelled) return
        setResult(data)
        setStatus('ready')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const kind = handleApiError(error)
        if (kind === null) return
        setListErrorKind(kind)
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [page, search, roleFilter, reloadKey, handleApiError])

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  function openCreate() {
    setPanel({ mode: 'create' })
    setTargetUserId('')
    setTargetRole('ADMIN')
    setFormError('')
    setSuccess('')
  }

  function openEdit(membership: PlatformMembership) {
    setPanel({ mode: 'edit', membership })
    setTargetUserId(membership.userId)
    setTargetRole(membership.role)
    setFormError('')
    setSuccess('')
  }

  function closePanel() {
    setPanel(null)
    setFormError('')
  }

  function mapApiError(error: unknown, mode: 'create' | 'edit'): string {
    if (!(error instanceof ApiError)) return t('platform.administrators.saveError')
    if (error.code === 'LAST_SUPERADMIN') return t('platform.administrators.lastSuperadmin')
    if (error.status === 404) {
      return mode === 'create'
        ? t('platform.administrators.userNotFound')
        : t('platform.administrators.membershipNotFound')
    }
    if (error.status === 409) return t('platform.administrators.alreadyMember')
    if (error.status === 403) return t('platform.administrators.forbidden')
    if (error.status === 400) return t('platform.administrators.invalidUserId')
    return error.message || t('platform.administrators.saveError')
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (saving || !panel) return

    const userId = targetUserId.trim()
    if (panel.mode === 'create' && !UUID_RE.test(userId)) {
      setFormError(t('platform.administrators.invalidUserId'))
      return
    }

    setSaving(true)
    setFormError('')
    try {
      if (panel.mode === 'create') {
        await createPlatformMembership({ userId, role: targetRole })
        setSuccess(t('platform.administrators.created'))
      } else {
        await updatePlatformMembership(userId, { role: targetRole })
        setSuccess(t('platform.administrators.updated'))
      }
      closePanel()
      setReloadKey(current => current + 1)
    } catch (error: unknown) {
      if (handleApiError(error) === null) return
      setFormError(mapApiError(error, panel.mode))
    } finally {
      setSaving(false)
    }
  }

  async function handleRevoke(membership: PlatformMembership) {
    if (revokingId) return
    if (!window.confirm(t('platform.administrators.revokeConfirm', { email: membership.email }))) {
      return
    }

    setRevokingId(membership.userId)
    setSuccess('')
    setFormError('')
    try {
      await deletePlatformMembership(membership.userId)
      setSuccess(t('platform.administrators.revoked'))
      setReloadKey(current => current + 1)
    } catch (error: unknown) {
      if (handleApiError(error) === null) return
      setFormError(mapApiError(error, 'edit'))
    } finally {
      setRevokingId(null)
    }
  }

  function canManage(membership: PlatformMembership): boolean {
    if (membership.userId === currentUserId) return false
    return canGrantSuperadmin || membership.role !== 'SUPERADMIN'
  }

  const hasFilters = search.length > 0 || roleFilter.length > 0

  return (
    <section className="page-section page-section--wide">
      <div className="page-header">
        <div>
          <h1 className="platform-page-title">{t('platform.administrators.title')}</h1>
          <p className="page-section-description">{t('platform.administrators.subtitle')}</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          {t('platform.administrators.assign')}
        </button>
      </div>

      {success && <div className="form-success-banner" role="status">{success}</div>}
      {formError && !panel && <div className="form-error-banner" role="alert">{formError}</div>}

      <div className="platform-toolbar">
        <form className="platform-search" onSubmit={handleSearch}>
          <label className="platform-visually-hidden" htmlFor="administratorSearch">
            {t('platform.administrators.search')}
          </label>
          <input
            id="administratorSearch"
            type="search"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            placeholder={t('platform.administrators.searchPlaceholder')}
            maxLength={100}
            autoComplete="off"
          />
          <button type="submit" className="btn btn-primary">
            {t('platform.administrators.search')}
          </button>
        </form>

        <div className="platform-filter">
          <label htmlFor="administratorRole">{t('platform.administrators.roleFilter')}</label>
          <select
            id="administratorRole"
            value={roleFilter}
            onChange={event => {
              setRoleFilter(event.target.value)
              setPage(1)
            }}
          >
            <option value="">{t('platform.administrators.allRoles')}</option>
            <option value="SUPERADMIN">{t('platform.role.SUPERADMIN')}</option>
            <option value="ADMIN">{t('platform.role.ADMIN')}</option>
            <option value="MANAGER">{t('platform.role.MANAGER')}</option>
          </select>
        </div>
      </div>

      {status === 'loading' && (
        <div className="page-loading"><div className="spinner" /></div>
      )}

      {status === 'error' && (
        <div className="settings-card">
          <div className="form-error-banner" role="alert">
            {listErrorKind === 'unknown'
              ? t('platform.administrators.loadError')
              : t(`platform.errors.${listErrorKind}`)}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setReloadKey(current => current + 1)}
          >
            {t('error.retry')}
          </button>
        </div>
      )}

      {status === 'ready' && result.data.length === 0 && (
        <div className="empty-state">
          <h3>{hasFilters ? t('platform.administrators.noResults') : t('platform.administrators.empty')}</h3>
          <p>{hasFilters ? t('platform.administrators.noResultsHint') : t('platform.administrators.emptyHint')}</p>
        </div>
      )}

      {status === 'ready' && result.data.length > 0 && (
        <>
          <div className="platform-table-wrap">
            <table className="platform-table">
              <thead>
                <tr>
                  <th>{t('platform.administrators.columns.user')}</th>
                  <th>{t('platform.administrators.columns.email')}</th>
                  <th>{t('platform.administrators.columns.role')}</th>
                  <th>{t('platform.administrators.columns.status')}</th>
                  <th>{t('platform.administrators.columns.createdAt')}</th>
                  <th aria-label={t('platform.administrators.columns.actions')} />
                </tr>
              </thead>
              <tbody>
                {result.data.map(membership => {
                  const displayName = [membership.firstName, membership.lastName].filter(Boolean).join(' ')
                    || membership.email
                  const manageable = canManage(membership)
                  const isSelf = membership.userId === currentUserId
                  return (
                    <tr key={membership.id}>
                      <td>
                        <span className="platform-table-name">{displayName}</span>
                        <code className="platform-code platform-table-sub">{membership.userId}</code>
                      </td>
                      <td>{membership.email}</td>
                      <td>{t(`platform.role.${membership.role}`)}</td>
                      <td>
                        <span className={`status-badge status-badge--${membership.isActive ? 'active' : 'archived'}`}>
                          {membership.isActive
                            ? t('platform.administrators.statusActive')
                            : t('platform.administrators.statusInactive')}
                        </span>
                      </td>
                      <td>{formatDate(membership.createdAt, i18n.language)}</td>
                      <td className="platform-table-actions">
                        {isSelf && (
                          <span className="field-hint">{t('platform.administrators.selfHint')}</span>
                        )}
                        {!isSelf && !manageable && (
                          <span className="field-hint">{t('platform.administrators.superadminHint')}</span>
                        )}
                        {manageable && (
                          <>
                            <button
                              type="button"
                              className="btn btn-ghost"
                              onClick={() => openEdit(membership)}
                            >
                              {t('platform.administrators.editRole')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger"
                              disabled={revokingId === membership.userId}
                              onClick={() => void handleRevoke(membership)}
                            >
                              {revokingId === membership.userId
                                ? t('platform.administrators.revoking')
                                : t('platform.administrators.revoke')}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {result.meta.totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page <= 1}
                onClick={() => setPage(current => Math.max(1, current - 1))}
              >
                {t('platform.administrators.previous')}
              </button>
              <span className="pagination-label">
                {t('platform.administrators.pageOf', {
                  page: result.meta.page,
                  total: result.meta.totalPages,
                })}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= result.meta.totalPages}
                onClick={() => setPage(current => current + 1)}
              >
                {t('platform.administrators.next')}
              </button>
            </div>
          )}
        </>
      )}

      <SidePanel
        open={panel !== null}
        onClose={closePanel}
        title={panel?.mode === 'edit'
          ? t('platform.administrators.editTitle')
          : t('platform.administrators.createTitle')}
        description={panel?.mode === 'edit'
          ? t('platform.administrators.editDescription')
          : t('platform.administrators.createDescription')}
      >
        <form onSubmit={event => void handleSubmit(event)}>
          <div className="form-field">
            <label htmlFor="administratorUserId">{t('platform.administrators.userId')}</label>
            <input
              id="administratorUserId"
              type="text"
              value={targetUserId}
              onChange={event => {
                setTargetUserId(event.target.value)
                setFormError('')
              }}
              readOnly={panel?.mode === 'edit'}
              disabled={saving}
              autoComplete="off"
              spellCheck={false}
            />
            <span className="field-hint">
              {panel?.mode === 'edit'
                ? t('platform.administrators.userIdReadonly')
                : t('platform.administrators.userIdHint')}
            </span>
          </div>

          <div className="form-field">
            <label htmlFor="administratorRoleSelect">{t('platform.administrators.role')}</label>
            <select
              id="administratorRoleSelect"
              value={targetRole}
              onChange={event => {
                setTargetRole(event.target.value as PlatformRole)
                setFormError('')
              }}
              disabled={saving}
            >
              {availableRoles.map(role => (
                <option key={role} value={role}>{t(`platform.role.${role}`)}</option>
              ))}
            </select>
            <span className="field-hint">{t('platform.administrators.managerHint')}</span>
          </div>

          {panel?.mode === 'create' && (
            <p className="field-hint">{t('platform.administrators.registrationHint')}</p>
          )}

          {formError && <div className="form-error-banner" role="alert">{formError}</div>}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('platform.administrators.saving') : t('platform.administrators.save')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={closePanel} disabled={saving}>
              {t('menu.cancel')}
            </button>
          </div>
        </form>
      </SidePanel>
    </section>
  )
}
